import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileCheck, AlertCircle } from 'lucide-react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    if (password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);

    try {
      await register(name, email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      {/* Form Section */}
      <div className="auth-form-section">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-forest rounded-sm flex items-center justify-center">
              <FileCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-xl font-bold tracking-tight text-forest">BustaControllo</span>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold tracking-tight mb-2">Crea il tuo account</h1>
          <p className="text-muted-foreground mb-8">
            Inizia a controllare le tue buste paga gratuitamente
          </p>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-4 mb-6 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="label-uppercase">Nome completo</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Mario Rossi"
                className="swiss-input h-12"
                data-testid="register-name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="label-uppercase">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@esempio.com"
                className="swiss-input h-12"
                data-testid="register-email"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="label-uppercase">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimo 6 caratteri"
                className="swiss-input h-12"
                data-testid="register-password"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="label-uppercase">Conferma Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ripeti la password"
                className="swiss-input h-12"
                data-testid="register-confirm-password"
                required
              />
            </div>

            <Button
              type="submit"
              className="swiss-btn-primary w-full h-12"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner" />
                  Registrazione...
                </span>
              ) : (
                'Crea account'
              )}
            </Button>
          </form>

          {/* Login link */}
          <p className="mt-8 pb-20 text-center text-sm text-muted-foreground">
            Hai già un account?{' '}
            <Link 
              to="/login" 
              className="text-forest font-medium hover:underline"
              data-testid="login-link"
            >
              Accedi
            </Link>
          </p>
          
          {/* Copyright */}
          <p className="mt-4 text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} BustaControllo. Tutti i diritti riservati.
          </p>
        </div>
      </div>

      {/* Brand Section */}
      <div className="auth-brand-section">
        <div className="relative z-10 text-white text-center max-w-md">
          <h2 className="text-4xl font-bold mb-6">
            Il controllo delle buste paga diventa semplice
          </h2>
          <p className="text-lg text-white/80">
            Carica i dati della tua busta paga e verifica automaticamente 
            tutti i calcoli secondo la normativa italiana vigente.
          </p>
          
          {/* Benefits */}
          <div className="mt-12 space-y-4 text-left">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mt-0.5">
                <span className="text-xs">✓</span>
              </div>
              <div>
                <div className="font-medium">Verifica istantanea</div>
                <div className="text-sm text-white/70">Risultati in pochi secondi</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mt-0.5">
                <span className="text-xs">✓</span>
              </div>
              <div>
                <div className="font-medium">Storico completo</div>
                <div className="text-sm text-white/70">Confronta mesi diversi</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mt-0.5">
                <span className="text-xs">✓</span>
              </div>
              <div>
                <div className="font-medium">Report dettagliati</div>
                <div className="text-sm text-white/70">Individua ogni anomalia</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
